import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from "@angular/core";
import {FUIPopsService} from "./pops.service";
import {Subscription} from "rxjs";
import {FUIResponsiveService} from "../page/responsive.service";
import {FUIEventsService} from "../events/events.service";

export enum PopParentHorizontalAlignment {
    LEFT,
    RIGHT,
    CENTER
}

export enum PopParentVerticalAlignment {
    ABOVE,
    BELOW,
    DYNAMIC
}

@Directive()
export abstract class FUIAbstractPop implements OnInit, OnDestroy, OnChanges {
    @Input() horizontalOffset: number = 0;
    @Output() public popped$: EventEmitter<boolean>;
    @Output() public verticalAlignment$: EventEmitter<PopParentVerticalAlignment>;

    @HostBinding("style.top.px") public styleTopPx: number;
    @HostBinding("style.bottom.px") public styleBottomPx: number;
    @HostBinding("style.left.px") public styleLeftPx: number;
    @HostBinding("style.right.px") public styleRightPx: number;
    @HostBinding("style.height.px") public styleHeightPx: number;
    @HostBinding("style.width") public styleWidth: string;
    @HostBinding("style.width.px") public styleWidthPx: number;
    @HostBinding("style.max-width.px") public styleMaxWidthPx: number;
    @HostBinding("style.transform") public styleTransform: string;
    @HostBinding("style.visibility") public styleVisibility: string;
    @HostBinding("class.disabled") public disabled: boolean;
    @HostBinding("class.inverted") public inverted: boolean;
    @HostBinding("class.hShifted") protected hShifted: boolean;
    @HostBinding("class.vShifted") protected vShifted: boolean;
    protected abstract parentHorizontalAlignment: PopParentHorizontalAlignment;
    protected abstract parentVerticalAlignment: PopParentVerticalAlignment;
    protected abstract limitWidthToParent: boolean;
    protected abstract adjustHeightToFirstElement: boolean;
    protected exclusive: boolean = true;
    protected _id: number;
    protected _$pop: Subscription;
    protected _$scroll: Subscription;
    protected _$window: Subscription;

    private minTop: number = 0;
    private minBottom: number = 0;

    constructor(protected changeDetectorRef: ChangeDetectorRef,
                public elementRef: ElementRef,
                protected eventsService: FUIEventsService,
                protected popsService: FUIPopsService,
                protected responsiveService: FUIResponsiveService) {
        this._id = Math.random();
        this.popped$ = new EventEmitter<boolean>();
        this.verticalAlignment$ = new EventEmitter<PopParentVerticalAlignment>();

        // This hack is to work around a long-standing iOS bug where fixed elements are clipped within scrolling ancestors
        // The values used are chosenfor WBO to allow for page headers and footers.
        if (this.responsiveService.isIos) {
            this.minTop = 165;
            this.minBottom = 85;
        }
    }

    @HostBinding("class.popped") protected _popped: boolean;
    @HostBinding("class.ready") protected ready: boolean;

    /**
     *
     * @returns {boolean}
     */
    public get popped(): boolean {
        return this._popped;
    }

    /**
     * Calculate the position and show or hide the popup
     * @param {boolean} popped
     */
    @Input()
    public set popped(popped: boolean) {
        this._popped = popped;

        if (this._popped === true) {
            this.locate();
            this.closeOthers();
        } else {
            this.ready = false;
        }
    }

    @HostBinding("class.leftAligned")
    public get isLeftAligned(): boolean {
        return this.parentHorizontalAlignment === PopParentHorizontalAlignment.LEFT;
    }

    @HostBinding("class.rightAligned")
    public get isRightAligned(): boolean {
        return this.parentHorizontalAlignment === PopParentHorizontalAlignment.RIGHT;
    }

    @HostBinding("class.above")
    public get isAbove(): boolean {
        return this.parentVerticalAlignment === PopParentVerticalAlignment.ABOVE || (this.parentVerticalAlignment === PopParentVerticalAlignment.DYNAMIC && this.inverted);
    }

    @HostBinding("class.below")
    public get isBelow(): boolean {
        return this.parentVerticalAlignment === PopParentVerticalAlignment.BELOW || (this.parentVerticalAlignment === PopParentVerticalAlignment.DYNAMIC && !this.inverted);
    }

    public ngOnInit(): void {
        this._$pop = this.popsService.pop$.subscribe(
            (pop) => {
                if (this.exclusive) {
                    if (this._id !== pop && this.popped) {
                        this.pop(false);
                    }
                }
            }
        );

        this._$scroll = this.eventsService.scroll$.subscribe(
            (scroll) => {
                if (this.popped) {
                    this.pop(false);
                    this.changeDetectorRef.markForCheck();
                }
            }
        );

        this._$window = this.responsiveService.window$.subscribe(
            (window) => {
                if (this.popped) {
                    this.locate();
                    this.closeOthers();
                }
            }
        );
    }

    public ngOnDestroy(): void {
        this._$pop.unsubscribe();
        this._$scroll.unsubscribe();
        this._$window.unsubscribe();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes["popped"] && this._popped === true) {
            setTimeout(() => {
                this.locate();
                this.closeOthers();
                this.ready = true;
            });
        }
    }

    private closeOthers() {
        if (this.exclusive) {
            this.popsService.pop = this._id;
        }
    }

    /**
     *
     * @param {boolean} popped
     */
    public pop(popped: boolean): void {
        if (this._popped !== popped) {
            this.popped = popped;
            this.popped$.emit(this.popped);
        }
    }

    get elementWidth(): number {
        return this.limitWidthToParent
            ? this.elementRef.nativeElement.parentNode.getBoundingClientRect().width
            : this.elementRef.nativeElement.clientWidth;
    }

    get elementHeight(): number {
        return this.adjustHeightToFirstElement
            ? this.elementRef.nativeElement.firstElementChild.clientHeight
            : this.elementRef.nativeElement.clientHeight;
    }

    /**
     * Calculate required height based on the maximum height if set, as the content may change later, expanding the element height.
     */
    get requiredHeight(): number {
        const maxHeight: string = this.adjustHeightToFirstElement
            ? window.getComputedStyle(this.elementRef.nativeElement.firstElementChild).maxHeight
            : window.getComputedStyle(this.elementRef.nativeElement).maxHeight;
        return maxHeight && maxHeight !== "none" ? Number(maxHeight.replace("px", "")) : this.elementHeight;
    }

    /**
     * Find and locate appropriately in relation to the parent element.
     * Using relative elements to the pop, find the correct placement within the page.
     * As the element will be fixed, it will be relative to the screen, not to any other element.
     */
    public locate(): void {
        let parentNode: any = this.elementRef.nativeElement.parentNode;
        if (parentNode == undefined) {
            return;
        }

        const elementWidth: number = this.elementWidth;
        const elementHeight: number = this.elementHeight;
        const requiredHeight: number = this.requiredHeight;

        if (this.limitWidthToParent) {
            this.styleMaxWidthPx = elementWidth;
            this.styleWidth = "100%";
        }
        if (this.adjustHeightToFirstElement) {
            this.styleHeightPx = elementHeight;
        }

        if (elementWidth === 0 || elementHeight === 0) {
            this.styleVisibility = "hidden";
            return;
        }
        this.styleVisibility = "visible";

        // In IOS/Safari when the keyboard opens it pushes the whole body up out screen so an offset needs to be added to the top of the popup
        const fixedHeaderOffSet: number = document.body.getBoundingClientRect().top < 0 ? 0 - document.body.getBoundingClientRect().top : 0;

        this.hShifted = false;
        this.vShifted = false;
        this.inverted = false;

        switch (this.parentHorizontalAlignment) {
            case PopParentHorizontalAlignment.LEFT:
                this.styleLeftPx = parentNode.getBoundingClientRect().left - this.horizontalOffset;
                break;
            case PopParentHorizontalAlignment.CENTER:
                this.styleLeftPx = parentNode.getBoundingClientRect().left + (parentNode.getBoundingClientRect().width - elementWidth) / 2;
                break;
            case PopParentHorizontalAlignment.RIGHT:
                this.styleLeftPx = parentNode.getBoundingClientRect().left + parentNode.getBoundingClientRect().width - elementWidth + this.horizontalOffset;
                break;
        }
        this.styleRightPx = undefined;
        if (this.styleLeftPx < 0) {
            this.styleLeftPx = 0;
            this.hShifted = true;
        } else if (this.styleLeftPx + elementWidth > this.responsiveService.host().innerWidth) {
            this.styleRightPx = 0;
            this.styleLeftPx = undefined;
            this.hShifted = true;
        }

        switch (this.parentVerticalAlignment) {
            case PopParentVerticalAlignment.ABOVE:
                this.styleBottomPx = this.responsiveService.host().innerHeight - parentNode.getBoundingClientRect().top;
                this.styleTopPx = undefined;
                this.verticalAlignment$.emit(PopParentVerticalAlignment.ABOVE);
                break;
            case PopParentVerticalAlignment.BELOW:
                this.styleTopPx = parentNode.getBoundingClientRect().top + parentNode.getBoundingClientRect().height + fixedHeaderOffSet;
                this.styleBottomPx = undefined;
                this.verticalAlignment$.emit(PopParentVerticalAlignment.BELOW);
                break;
            case PopParentVerticalAlignment.DYNAMIC:
                // Calculate whether to pop below (preferred) or above (if insufficient space below) the parent node
                if (this.responsiveService.host().innerHeight >= parentNode.getBoundingClientRect().top + parentNode.getBoundingClientRect().height + requiredHeight) {
                    // Use the parent node's height and top position to find the top of the popup
                    this.styleTopPx = parentNode.getBoundingClientRect().top + parentNode.getBoundingClientRect().height + fixedHeaderOffSet;
                    this.styleBottomPx = undefined;
                    this.verticalAlignment$.emit(PopParentVerticalAlignment.BELOW);
                } else {
                    // Use the parent node's height and window height to find the bottom of the popup
                    this.inverted = true;
                    this.styleBottomPx = this.responsiveService.host().innerHeight - parentNode.getBoundingClientRect().top;
                    this.styleTopPx = undefined;
                    this.verticalAlignment$.emit(PopParentVerticalAlignment.ABOVE);
                }
                break;
        }
        if (this.styleTopPx !== undefined) {
            if (this.styleTopPx < this.minTop) {
                this.styleTopPx = this.minTop;
                this.vShifted = true;
            } else if (this.styleTopPx + requiredHeight > this.responsiveService.host().innerHeight - this.minBottom) {
                this.styleBottomPx = this.minBottom;
                this.styleTopPx = undefined;
                this.vShifted = true;
            }
        } else if (this.styleBottomPx !== undefined) {
            if (this.styleBottomPx < this.minBottom) {
                this.styleBottomPx = this.minBottom;
                this.vShifted = true;
            } else if (this.styleBottomPx + requiredHeight > this.responsiveService.host().innerHeight) {
                if (this.inverted) {
                    // No room above, so un-invert and attach to bottom of screen
                    this.styleBottomPx = this.minBottom;
                    this.inverted = false;
                } else {
                    this.styleTopPx = this.minTop;
                    this.styleBottomPx = undefined;
                }
                this.vShifted = true;
            }
        }
        this.changeDetectorRef.markForCheck();

        // Address observed behaviour where size of popup changes immediately after calculating location.
        setTimeout(() => {
            if (this.elementWidth !== elementWidth || this.elementHeight !== elementHeight) {
                this.locate();
            }
        });
    }
}
